import type { ComponentFactory, ComponentRef, Type } from '@angular/core';
import type { WidgetErrors } from '@common/types/widget-errors.type';
import type { WebLegacyWidgetEntity, WebWidgetControlComponent } from '@web/types';
import type { WidgetsErrorsState } from './widgets-errors.reducer';

import { ComponentFactoryResolver, Injectable, Injector, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map } from 'rxjs/operators';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';

import WidgetsErrorsActions from '@web/store/page-structure/widgets-errors/widgets-errors.actions';
import WidgetsLegacyActions, { WidgetsActions } from '@web/store/page-structure/widgets/widgets.actions';

import { Widgets } from '@common/enums';
import { widgetNameOf } from '@common/helpers/widget-envelope';

/**
 * The widgets whose checks still run in their control, built bare for the page's initial errors. Every
 * other control inherits a `validate()` that finds nothing; the form and payment-form widgets validate in
 * their signal form (see `selectWidgetsRuleErrors`).
 */
const CONTROL_VALIDATED_WIDGETS = new Set<Widgets | undefined>([Widgets.PRODUCT_CARD, Widgets.SHOP_CART]);

@Injectable()
export class WidgetsErrorsEffects {
    private readonly actions$ = inject(Actions);
    private readonly injector = inject(Injector);
    private readonly componentFactoryResolver = inject(ComponentFactoryResolver);
    private readonly widgetsService = inject(WebWidgetsService);

    public readonly setWidgetsErrors = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.init),
            map(({ widgets }) => {
                const errors = widgets.reduce((result: WidgetsErrorsState, widget: WebLegacyWidgetEntity) => {
                    const name = widgetNameOf(widget);
                    if (!CONTROL_VALIDATED_WIDGETS.has(name)) {
                        return result;
                    }

                    const component: Type<WebWidgetControlComponent> =
                        this.widgetsService.getWidgetControlComponent(name);
                    if (!component) {
                        return result;
                    }

                    const componentFactory: ComponentFactory<WebWidgetControlComponent> =
                        this.componentFactoryResolver.resolveComponentFactory(component);
                    const componentRef: ComponentRef<WebWidgetControlComponent> = componentFactory.create(
                        this.injector
                    );

                    const validating = componentRef.instance as unknown as {
                        validate(widget: WebLegacyWidgetEntity): WidgetErrors;
                    };
                    const widgetErrors = validating.validate(widget);
                    componentRef.destroy();

                    if (!widgetErrors || !widgetErrors.length) {
                        return result;
                    }

                    result[widget.id] = {
                        label: widgetNameOf(widget),
                        errors: widgetErrors,
                    };

                    return result;
                }, {});

                return WidgetsErrorsActions.setWidgetsErrors({ errors });
            })
        )
    );

    public readonly removeWidget = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.removeWidget, WidgetsActions.removed),
            map(({ widget }) => WidgetsErrorsActions.removeWidgetErrors({ widgetId: widget.id }))
        )
    );
}
